import React, { useState, useEffect, useRef } from 'react';
import './index.css';
import { useUser } from '../UserContext';
import { useNavigate, Link } from 'react-router-dom';

// Importações do Chart.js para renderização nativa
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';

// Registrando os módulos necessários do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// --- SUBCOMPONENTE PARA PROCESSAR E RENDERIZAR GRÁFICOS OU TEXTO ---
const ChatMessageBubble = ({ msg }) => {
  if (msg.sender === 'user') {
    return <div className="chat-bubble user-bubble">{msg.text}</div>;
  }

  // Regex para capturar o conteúdo entre as tags [GRAFICO_START] e [GRAFICO_END]
  const graficoRegex = /\[GRAFICO_START\]([\s\S]*?)\[GRAFICO_END\]/;
  const temGrafico = msg.text.match(graficoRegex);

  if (!temGrafico) {
    // Se não tiver gráfico, renderiza o texto normal
    return <div className="chat-bubble gemini-bubble">{msg.text}</div>;
  }

  // Se houver gráfico, separa o texto puro dos dados JSON do gráfico
  const textoPuro = msg.text.replace(graficoRegex, '').trim();
  let dadosDoGrafico = null;

  try {
    dadosDoGrafico = JSON.parse(temGrafico[1].trim());
  } catch (e) {
    console.error("Erro ao fazer o parse do JSON do gráfico:", e);
  }

  return (
    <div className="chat-bubble gemini-bubble">
      {/* Texto descritivo da IA */}
      {textoPuro && <div style={{ marginBottom: dadosDoGrafico ? '15px' : '0' }}>{textoPuro}</div>}

      {/* Renderização condicional do Gráfico baseado no tipo retornado */}
      {dadosDoGrafico && (
        <div className="chat-graphic-render" style={{ background: '#fff', padding: '10px', borderRadius: '8px', marginTop: '10px', width: '100%', maxWidth: '500px' }}>
          {dadosDoGrafico.tipo === 'bar' && (
            <Bar 
              data={{ labels: dadosDoGrafico.labels, datasets: dadosDoGrafico.datasets }} 
              options={{ responsive: true, plugins: { title: { display: true, text: dadosDoGrafico.titulo } } }} 
            />
          )}
          {dadosDoGrafico.tipo === 'line' && (
            <Line 
              data={{ labels: dadosDoGrafico.labels, datasets: dadosDoGrafico.datasets }} 
              options={{ responsive: true, plugins: { title: { display: true, text: dadosDoGrafico.titulo } } }} 
            />
          )}
          {dadosDoGrafico.tipo === 'pie' && (
            <Pie 
              data={{ labels: dadosDoGrafico.labels, datasets: dadosDoGrafico.datasets }} 
              options={{ responsive: true, plugins: { title: { display: true, text: dadosDoGrafico.titulo } } }} 
            />
          )}
        </div>
      )}
    </div>
  );
};

// --- COMPONENTE PRINCIPAL ---
const Assistente = () => {
  const { userInfo } = useUser();
  const navigate = useNavigate();
  const textareaRef = useRef(null); // Referência para controlar a altura do textarea

  useEffect(() => {
    if (!userInfo?.username) {
      localStorage.removeItem('userInfo');
      navigate('/');
      return;
    }
  }, [userInfo, navigate]);

  const [messages, setMessages] = useState([
    { id: 1, text: "Olá! Sou a Wendy sua assistente de vendas focado em NF-e. Ainda estou aprendendo, mas logo logo estarei pronta e com muita vontade de te ajudar a vender mais. Como posso te ajudar hoje?", sender: "gemini" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const credentials = btoa(`${userInfo?.alias};${userInfo?.venLogin}:${userInfo?.venSenha}`);

  // Ajusta a altura do campo de texto conforme o usuário digita linhas extras
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`; // Teto máximo de 120px de altura
    }
  }, [input]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const textoPergunta = input;
    const userMessage = { id: Date.now(), text: textoPergunta, sender: "user" };
    
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await fetch(`${userInfo.url}/api/assistente-vendas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pergunta: textoPergunta,
          urlApi: userInfo.url,
          alias: userInfo.alias,
          credentials: credentials, 
          venId: userInfo.ven_id,
          cliId: '',
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.resposta) {
        setMessages(prev => [...prev, { 
          id: Date.now() + 1, 
          text: data.resposta, 
          sender: "gemini" 
        }]);
      } else {
        throw new Error(data.erro || "Erro interno do servidor");
      }

    } catch (error) {
      console.error("Erro ao consultar o assistente:", error);
      setMessages(prev => [...prev, { 
        id: Date.now() + 1, 
        text: "Desculpe, tive um problema para consultar os dados das notas e gerar a resposta. Tente novamente mais tarde.", 
        sender: "gemini" 
      }]);
    } finally {
      setLoading(false);
    }
  };

  // Captura o comportamento do teclado dentro do campo de texto
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Impede a quebra de linha padrão do Enter simples
      handleSend();       // Dispara o envio
    }
    // Se for Shift + Enter, o comportamento nativo de quebra de linha é mantido
  };

  return (
    <>
      <header className="header">
        <img src={`${userInfo?.url}/imagens/${userInfo?.alias}.bmp`} alt="Logo da empresa" className='logoHeader' />
        <h2>Bem-vindo, {userInfo?.username}!</h2>
        <h1>Chat com IA {userInfo?.company}</h1>
      </header>

      <Link to="/menu" className="link-style">Voltar ao menu</Link>

      <div className="chat-container">
        <div className="chat-header">
          <div className="chat-title">Wendy — Assistente de NF-e</div>
          <div className="chat-subtitle">Análise de dados faturados em tempo real</div>
        </div>

        <div className="chat-messages">
          {messages.map(msg => (
            <div key={msg.id} className={`chat-message-row ${msg.sender === 'user' ? 'user-row' : 'gemini-row'}`}>
              {/* Usando o nosso subcomponente inteligente que sabe interpretar texto e gráficos */}
              <ChatMessageBubble msg={msg} />
            </div>
          ))}
          
          {loading && (
            <div className="chat-loading-row">
              <div className="chat-loading-spinner"></div>
              <span className="chat-loading-text">Analisando dados da base aguarde...</span>
            </div>
          )}
        </div>

        {/* Área de entrada adaptativa */}
        <div className="chat-input-area" style={{ alignItems: 'flex-end' }}>
          <textarea 
            ref={textareaRef}
            rows={1}
            value={input} 
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ex: Quais os produtos mais caros? (Use Shift+Enter para quebrar linha)" 
            className="chat-input-field"
            disabled={loading}
            style={{ 
              resize: 'none', 
              overflowY: 'auto',
              paddingTop: '10px',
              paddingBottom: '10px',
              lineHeight: '20px'
            }}
          />
          <button 
            onClick={handleSend} 
            className="chat-send-button"
            disabled={loading || !input.trim()}
            style={{ marginBottom: '5px' }} // Alinha o botão sutilmente com o textarea expandido
          >
            Enviar
          </button>
        </div>
      </div>
    </>
  );
};

export default Assistente;